import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import { VERSION } from '@/constants';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import TranslationService from '@/locale/translation.service';
import { IUserDetails } from '@/shared/model/user.model';
import { Role } from '@/shared/model/enumerations/role.model';

@Component
export default class JhiNavbar extends Vue {
  @Inject('loginService')
  private loginService: () => LoginService;
  @Inject('translationService') private translationService: () => TranslationService;

  @Inject('accountService') private accountService: () => AccountService;
  public version = VERSION ? 'v' + VERSION : '';
  private currentLanguage = this.$store.getters.currentLanguage;
  private languages: any = this.$store.getters.languages;
  private hasAnyAuthorityValue = false;
  private isAdmin: boolean = false;

  created() {
    this.translationService().refreshTranslation(this.currentLanguage);
  }

  public get menu() {
    return {
      items: [
        { title: this.$t('global.menu.dashboard'), icon: 'mdi-view-dashboard-outline', route: '/', isVisible: true },
        { title: this.$t('global.menu.entities.orders'), icon: 'mdi-cart-outline', route: '/order', isVisible: true },
        {
          title: this.$t('global.menu.entities.products'),
          icon: 'mdi-shopping-outline',
          route: '/product',
          isVisible: this.hasShopRole(Role.MANAGER),
        },
      ],
      settings: [
        {
          action: 'mdi-cog-outline',
          active: false,
          isVisible: this.hasShopRole(Role.MANAGER),
          items: [
            { title: this.$t('global.menu.entities.company'), route: '/company', isVisible: this.hasCompanyRole(Role.MANAGER) },
            { title: this.$t('global.menu.entities.userAccess'), route: '/user-access', isVisible: this.hasCompanyRole(Role.MANAGER) },
            { title: this.$t('global.menu.entities.shop'), route: '/shop', isVisible: this.hasCompanyRole(Role.EMPLOYEE) },
            { title: this.$t('global.menu.entities.userAccess'), route: '/user-shop', isVisible: this.hasCompanyRole(Role.EMPLOYEE) },
            { title: this.$t('global.menu.entities.productType'), route: '/product-type', isVisible: true },
            {
              title: this.$t('global.menu.entities.shopApprovalRule'),
              icon: 'mdi-scale-balance',
              route: '/shop-approval-rule',
              isVisible: true,
            },
          ],
          title: this.$t('global.menu.settings'),
        },
      ],
      adminSettings: [
        {
          action: 'mdi-view-grid-outline',
          active: false,
          items: [
            {
              title: this.$t('global.menu.entities.defaultEmailTemplate'),
              icon: 'mdi-email-newsletter',
              route: '/admin/email-template',
              isVisible: true,
            },
            {
              title: this.$t('global.menu.admin.userManagement'),
              icon: 'mdi-account-group',
              route: '/admin/user-management',
              isVisible: true,
            },
            { title: this.$t('global.menu.admin.metrics'), icon: 'mdi-speedometer', route: '/admin/metrics', isVisible: true },
            { title: this.$t('global.menu.admin.health'), icon: 'mdi-heart-pulse', route: '/admin/health', isVisible: true },
            { title: this.$t('global.menu.admin.configuration'), icon: 'mdi-tune', route: '/admin/configuration', isVisible: true },
            { title: this.$t('global.menu.admin.logs'), icon: 'mdi-format-list-bulleted-square', route: '/admin/logs', isVisible: true },
            { title: this.$t('global.menu.admin.apidocs'), icon: 'mdi-file-document', route: '/admin/docs', isVisible: true },
          ],
          title: 'Admin',
        },
      ],
    };
  }

  public subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    return paths.some(path => {
      return this.$route.path.indexOf(path) === 0; // current path starts with this path string
    });
  }

  public changeLanguage(newLanguage: string): void {
    this.translationService().refreshTranslation(newLanguage);
  }

  public isActiveLanguage(key: string): boolean {
    return key === this.$store.getters.currentLanguage;
  }

  public logout(): void {
    localStorage.removeItem('jhi-authenticationToken');
    sessionStorage.removeItem('jhi-authenticationToken');
    this.$store.commit('logout');
    this.$router.push('/', () => {});
  }

  public openLogin(): void {
    this.loginService().openLogin((<any>this).$root);
  }

  public get authenticated(): boolean {
    return this.$store.getters.authenticated;
  }

  public get drawer(): boolean {
    return this.$store.getters.drawer;
  }
  public set drawer(isOpen) {
    this.$store.commit('setDrawer', isOpen);
  }
  @Watch('hasAnyAuthorityValue')
  @Watch('isAdmin')
  public hasCompanyRole(companyRole: string): boolean {
    const activeEntity = this.$store.getters.activeEntity;
    if (activeEntity != null && !this.isAdmin) {
      const activeCompanyId = this.$store.getters.activeEntity.company.id;
      const companies = this.$store.getters.companies;
      const activeCompanyRole = companies.find(company => company.company.id === activeCompanyId);
      return activeCompanyRole.role == companyRole;
    }
    return companyRole === Role.MANAGER;
  }
  public hasShopRole(userRole: string): boolean {
    const activeEntity = this.$store.getters.activeEntity;
    if (activeEntity != null && !this.isAdmin) {
      const activeShopRole = this.$store.getters.activeEntity.role;
      return activeShopRole == userRole;
    }
    return userRole === Role.MANAGER;
  }
  public hasAnyAuthority(authorities: any): boolean {
    this.accountService()
      .hasAnyAuthorityAndCheckAuth(authorities)
      .then(value => {
        this.hasAnyAuthorityValue = value;
      });
    return this.hasAnyAuthorityValue;
  }

  public get openAPIEnabled(): boolean {
    return this.$store.getters.activeProfiles.indexOf('api-docs') > -1;
  }

  public get inProduction(): boolean {
    return this.$store.getters.activeProfiles.indexOf('prod') > -1;
  }

  public get activeShop(): number {
    return 0; // index
  }

  public get user(): IUserDetails | any {
    if (this.$store.getters.account) {
      const { id, firstName, lastName, email, login } = this.$store.getters.account;
      const fullName = firstName + ' ' + lastName;
      const initials = this.userInitials(fullName);
      return {
        id,
        email,
        login,
        initials,
        fullName,
      };
    }
    return {};
  }

  userInitials(fullName: string): string {
    const fullNameArr = fullName.split(' ');
    const initials = fullNameArr.shift().charAt(0) + fullNameArr.pop().charAt(0);
    return initials.toUpperCase();
  }
}
